import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import { catchError, Observable, of, Subject, tap } from 'rxjs';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { PromptService } from '../../components/prompt/prompt.service';
import { RemindersApiProviderService } from '../../data-providers/api-providers/reminders-api-provider/reminders-api-provider.service';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { TTranslationKey } from '../../features/translate/types';
import { getErrorMessage } from '../../helpers/database/get-error-message';
import { EIntercomEvent } from '../../services/intercom-service-consts';
import { IntercomService } from '../../services/intercom.service';
import { ResponseErrorService } from '../errors/response-error.service';
import { QuickReminderFilterService } from '../filters/filters-api/reminders-search/quick-reminder-filter.service';
import { ReminderSearchService } from './reminder-search.service';
import {
  TReminder,
  TReminderActionDTO,
  TReminderDTO,
  TReminderUpdateDTO,
} from './reminders.consts';

@Injectable({
  providedIn: 'root',
})
export class RemindersService {
  private reminders: TReminder[] = [];
  private dataFetched = false; // so we can cache fetching all reminders
  private fetchedTimestamp: number = null; // so we can cache fetching reminders for a specific timestamp

  private _remindersChanged = new Subject<TReminder[]>();
  public remindersChanged$ = this._remindersChanged.asObservable();

  constructor(
    private reminderSearchService: ReminderSearchService,
    private responseErrorService: ResponseErrorService,
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
    private intercomService: IntercomService,
    private remindersApiProviderService: RemindersApiProviderService,
    private quickReminderFilterService: QuickReminderFilterService,
  ) {
    this.quickReminderFilterService.getFiltersChange$().subscribe(() => {
      this.quickFetchReminders();
    });
  }

  quickFetchReminders(): void {
    this.fetchReminders(true).subscribe();
  }

  fetchReminders(forced: boolean = false): Observable<TReminder[]> {
    if (this.reminders.length && this.dataFetched && !forced) {
      return of(this.reminders);
    }

    return this.reminderSearchService.searchReminders().pipe(
      tap((reminders) => {
        this.dataFetched = true;
        this.reminders = reminders;
        this.emitRemindersChanged();
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_fetch_reminders_error')),
    );
  }

  fetchRemindersForSeries(seriesId: string): Observable<TReminder[]> {
    return this.reminderSearchService.searchReminders(seriesId).pipe(
      tap((reminders) => {
        this.updateRemindersForSeries(reminders, seriesId);
        this.emitRemindersChanged();
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_fetch_reminders_error')),
    );
  }

  getReminders(): TReminder[] {
    return this.reminders;
  }

  createReminder(body: TReminderDTO): Observable<TReminder> {
    return this.remindersApiProviderService.createReminder(body).pipe(
      tap((reminder) => {
        this.reminders.push(reminder);
        this.emitRemindersChanged();

        const prompt = this.translationPipe.transform('prompt_create_reminder_success');
        this.promptService.showSuccess(prompt);
        this.intercomService.trackIntercomEvent(EIntercomEvent.CREATE_REMINDER);
        this.intercomService.markUserCreatedReminder();
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_create_reminder_error')),
    );
  }

  getFilteredReminders(timestampUntil: number): Observable<TReminder[]> {
    if (this.fetchedTimestamp >= timestampUntil && this.reminders.length) {
      return of(
        this.reminders.filter((reminder) => reminder.timestampEpochMillis <= timestampUntil),
      );
    }

    return this.remindersApiProviderService.getFilteredReminders(timestampUntil).pipe(
      tap((reminders) => {
        this.updateReminders(reminders);
        this.fetchedTimestamp = timestampUntil;
        this.emitRemindersChanged();
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_fetch_reminders_error')),
    );
  }

  fetchRemindersForPoint(pointId: string): Observable<TReminder[]> {
    return this.remindersApiProviderService
      .getRemindersForPoint(pointId)
      .pipe(
        catchError((error) => this.handleRemindersError(error, 'prompt_fetch_reminders_error')),
      );
  }

  editReminder(reminderId: string, body: TReminderUpdateDTO): Observable<TReminder> {
    return this.remindersApiProviderService.editReminder(reminderId, body).pipe(
      tap((reminder) => {
        const reminderIndex = this.reminders.findIndex((r) => r.reminderId === reminder.reminderId);
        this.reminders[reminderIndex] = reminder;

        this.emitRemindersChanged();

        const prompt = this.translationPipe.transform('prompt_edit_reminder_success');
        this.promptService.showSuccess(prompt);
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_edit_reminder_error')),
    );
  }

  deleteReminder(reminderId: string): Observable<null> {
    return this.remindersApiProviderService.deleteReminder(reminderId).pipe(
      tap(() => {
        const reminderIndex = this.reminders.findIndex((r) => r.reminderId === reminderId);
        this.reminders.splice(reminderIndex, 1);
        this.emitRemindersChanged();

        const prompt = this.translationPipe.transform('prompt_delete_reminder_success');
        this.promptService.showSuccess(prompt);
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_delete_reminder_error')),
    );
  }

  private checkIfSomeRemindersGotDeleted(
    reminderIds: string[],
    responseRemindersList: string[],
  ): void {
    if (reminderIds.length !== responseRemindersList.length) {
      const errorPrompt = this.translationPipe.transform('prompt_reminder_not_found');

      this.promptService.showError(errorPrompt);
    }
  }

  snoozeReminders(body: TReminderActionDTO): Observable<TReminder[]> {
    return this.remindersApiProviderService.snoozeReminders(body).pipe(
      tap((reminders) => {
        reminders.forEach((reminder) => {
          const reminderIndex = this.reminders.findIndex(
            (r) => r.reminderId === reminder.reminderId,
          );
          this.reminders[reminderIndex] = reminder;
        });

        this.checkIfSomeRemindersGotDeleted(
          body.reminderIds,
          reminders.map((r) => r.reminderId),
        );

        this.emitRemindersChanged();

        if (reminders.length !== 0) {
          const prompt = this.translationPipe.transform('prompt_snooze_reminder_success');
          this.promptService.showSuccess(prompt);
        }
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_snooze_reminder_error')),
    );
  }

  dismissReminders(body: TReminderActionDTO): Observable<TReminder[]> {
    return this.remindersApiProviderService.dismissReminders(body).pipe(
      tap((reminders) => {
        reminders.forEach((reminder) => {
          const reminderIndex = this.reminders.findIndex(
            (r) => r.reminderId === reminder.reminderId,
          );
          this.reminders[reminderIndex] = reminder;
        });

        this.checkIfSomeRemindersGotDeleted(
          body.reminderIds,
          reminders.map((r) => r.reminderId),
        );

        this.emitRemindersChanged();

        if (reminders.length !== 0) {
          const prompt = this.translationPipe.transform('prompt_dismiss_reminder_success');
          this.promptService.showSuccess(prompt);
        }
      }),
      catchError((error) => this.handleRemindersError(error, 'prompt_dismiss_reminder_error')),
    );
  }

  clearData(): void {
    this.reminders = [];
    this.dataFetched = false;
    this.fetchedTimestamp = null;

    this.emitRemindersChanged();
  }

  updateRemindersFromSockets(reminders: TReminder[]): void {
    this.updateReminders(reminders);
    this.emitRemindersChanged();
  }

  updateReminders(reminders: TReminder[]): void {
    reminders.forEach((reminder) => {
      const reminderIndex = this.reminders.findIndex((r) => r.reminderId === reminder.reminderId);
      if (reminderIndex === -1) {
        this.reminders.push(reminder);
      } else {
        this.reminders[reminderIndex] = reminder;
      }
    });
  }

  private updateRemindersForSeries(reminders: TReminder[], seriesId: string): void {
    let newReminders = cloneDeep(this.reminders);

    reminders.forEach((reminder) => {
      const reminderIndex = this.reminders.findIndex((r) => r.reminderId === reminder.reminderId);
      if (reminderIndex === -1) {
        newReminders.push(reminder);
      } else {
        newReminders[reminderIndex] = reminder;
      }
    });

    newReminders = newReminders.filter((reminder) => {
      const isInSeries = reminder.seriesId === seriesId;

      if (!isInSeries) {
        return true;
      }

      return reminders.some((r) => r.reminderId === reminder.reminderId);
    });

    this.reminders = newReminders;
  }

  removeRemindersFromSeries(seriesId: string): void {
    this.reminders = this.reminders.filter((reminder) => reminder.seriesId !== seriesId);
    this.emitRemindersChanged();
  }

  private handleRemindersError(error: any, errorKey: TTranslationKey): Observable<never> {
    if (error.status === EStatusCode.BAD_REQUEST) {
      getErrorMessage(error).then((message) => {
        this.promptService.showError(message);
      });
    } else if (error.status === EStatusCode.NOT_FOUND) {
      const prompt = this.translationPipe.transform('prompt_reminder_not_found');

      this.promptService.showError(prompt);
    } else {
      const prompt = this.translationPipe.transform(errorKey);

      this.promptService.showError(prompt);
    }

    return this.responseErrorService.handleRequestError(
      error,
      'Failed to process reminders request',
    );
  }

  private emitRemindersChanged(): void {
    this._remindersChanged.next(this.reminders);
  }
}
